<template>
  <b-row class="m-0" :class="{ 'd-none': !showCaptcha }">
    <b-col class="position-relative padding-x-0" :class="boxClass">
      <div class="captcha-style">
        <div class="captcha-style__input">
          <b-form-input
            v-model="inputValue"
            :type="type"
            :id="id"
            :minLength="minLength"
            :maxLength="maxLength"
            :autocomplete="autocomplete"
            :disabled="disabled || loading"
            :state="generateState"
            :aria-describedby="`${id}-help ${id}-feedback`"
            :placeholder="placeholder"
            trim
            class="input"
            @input="changeCaptcha($event)"
            @blur="blur"
          />
        </div>
        <div class="captcha-style__image">
          <b-img fluid :src="captchaImage" />
        </div>
        <div class="position-relative">
          <PieChart
            class="position-absolute top--20 right--19 height-30"
            :width="40"
            :height="40"
            :series="series"
            :colors="generateColors"
          />
          <Button
            class="captcha-style__button"
            :contentClass="`justify-content-center ${loading ? 'spinner' : ''}`"
            variant="outline-primary"
            justIcon="refresh"
            :disabled="loading"
            @click="handleGetCaptcha"
          />
        </div>
      </div>
      <FeedbackMessages
        :state="generateState"
        :label="label"
        :vuelidateObj="vuelidateObj"
      />
    </b-col>
  </b-row>
</template>
<script>
import { Pie as PieChart } from "@/components/charts";

import { Button } from "@/components/re-design/forms";
import FeedbackMessages from "../feedback-messages/FeedbackMessages.vue";
import { pathOr } from "ramda";
import { generateApiUrl, generateError, storeDispatcher } from "@/shared/utils";
import { getCaptcha } from "@/services";
import { mapGetters } from "vuex";

export default {
  components: {
    Button,
    FeedbackMessages,
    PieChart,
  },
  props: {
    value: {}, // watch model
    name: {
      // key for update value
      type: String,
      default: null,
    },
    label: {
      // input label
      type: String,
      default: "کد امنیتـی",
    },
    id: {
      // input id
      type: String,
      default: "default-id",
    },
    type: {
      // input type
      type: String,
      default: "text",
    },
    placeholder: {
      // input placeholder
      type: String,
      default: "کد امنیتـی",
    },
    autocomplete: {
      // input autocomplete
      type: String,
      default: "off",
    },
    disabled: {
      // input disabled state
      type: Boolean,
      default: false,
    },
    vuelidateObj: {
      // vuelidate object of input
      type: Object,
      default: null,
    },
    minLength: {
      // input minLength
      type: [String, Number],
      default: 5,
    },
    maxLength: {
      // input maxLength
      type: [String, Number],
      default: 5,
    },
    forceShow: {
      type: Boolean,
      default: false,
    },
    boxClass: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      captchaKey: null,
      captchaImage: null,
      show: false,
      timerQueue: null,
      progressWidth: 100,
      clientStartedTime: null,
      negativeProgress: 0,
      positiveProgress: 100,
    };
  },
  computed: {
    ...mapGetters(["shouldRefreshCaptcha"]),
    inputValue: {
      /* guidance: watching input value */
      get() {
        return this.value;
      },
      set(val) {
        const value = val ? val.toUpperCase() : null;
        this.$emit("input", this.name, value);
      },
    },
    showCaptcha() {
      return this.show || this.forceShow;
    },
    generateState() {
      return this.vuelidateObj.$dirty ? !this.vuelidateObj.$error : null;
    },
    series() {
      return [this.positiveProgress, this.negativeProgress];
    },
    generateColors() {
      let colors = ["#0085FF", "#E0F0FF"];
      if (this.positiveProgress > 50) {
        colors = ["#0085FF", "#E0F0FF"];
      } else if (this.positiveProgress > 25) {
        colors = ["#FDAB3D", "#E0F0FF"];
      } else {
        colors = ["#D83A52", "#E0F0FF"];
      }

      return colors;
    },
  },
  watch: {
    shouldRefreshCaptcha: {
      immediate: true,
      handler(value) {
        if (value) {
          this.refresh();
          setTimeout(
            () => storeDispatcher("shouldRefreshCaptcha", false),
            1000
          );
        }
      },
    },
  },
  mounted() {
    this.handleGetCaptcha();
    const input = document.getElementById(this.id);
    input.setAttribute("value", "");
    input.addEventListener("keyup", () => {
      input.setAttribute("value", input.value);
    });
  },
  destroyed() {
    this.reset();
  },
  methods: {
    pathOr,
    generateApiUrl,
    focus() {
      this.$emit("focus", this.name);
    },
    blur() {
      this.$emit("blur", this.name, this.inputValue);
    },
    changeCaptcha(ev) {
      const regular = /^[\u0600-\u06FF\s]+$/;
      if (ev.keyCode == 8 || ev.keyCode == 46) return;
      if (
        this.inputValue &&
        this.inputValue.length === 1 &&
        regular.test(this.inputValue)
      ) {
        return this.$notify.error(this.$lang.errors.persianCharacters);
      }
      this.$emit("getKey", this.captchaKey);
    },
    reset() {
      this.progressWidth = 0;
      clearInterval(this.timerQueue);
    },
    async handleGetCaptcha() {
      if (this.loading) return;
      try {
        this.inputValue = null;
        this.loading = true;
        const response = await getCaptcha();
        const { resCaptchaImage, resCaptchaKey, resExpireSeconds, resShow } =
          response;
        this.captchaImage = resCaptchaImage;
        this.captchaKey = resCaptchaKey;
        this.show = resShow;
        this.$emit("setShowCaptcha", this.show);

        // started time
        this.clientStartedTime = new Date();
        this.timer(resExpireSeconds);
      } catch (error) {
        generateError(error);
      } finally {
        this.loading = false;
      }
    },
    timer(expireSeconds = 300) {
      let distance = expireSeconds;
      clearInterval(this.timerQueue);
      this.timerQueue = setInterval(() => {
        // check different started time with now time
        const startedTime = new Date(this.clientStartedTime).getTime();
        const nowTime = new Date().getTime();
        const differentTimeSeconds = Math.round((nowTime - startedTime) / 1000);
        distance = expireSeconds - differentTimeSeconds;
        distance -= 1;
        distance = (distance / expireSeconds) * 100;
        this.negativeProgress = 100 - distance;
        this.positiveProgress = distance;
        if (distance < 0) {
          this.negativeProgress = 0;
          this.positiveProgress = 100;
          clearInterval(this.timerQueue);
          this.handleGetCaptcha();
        }
      }, 1000);
    },
    refresh() {
      this.reset();
      this.handleGetCaptcha();
    },
  },
};
</script>
